import { Checkbox, type CheckboxProps } from '@progress/kendo-react-inputs'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { Box } from '~/components/elements/Box'
import { HStack } from '~/components/elements/HStack'
import {
  IconButton,
  type IconButtonProps,
} from '~/components/elements/IconButton'
import {
  StyledInput,
  type StyledInputProps,
} from '~/components/elements/StyledInput'
import { Typography } from '~/components/elements/Typography'
import { AnswerOptionConditionalSelector } from '~/components/features/AnswerOptionConditionalSelector'
import {
  ConditionOperationSelector,
  type ConditionOperationSelectorProps,
} from '~/components/features/ConditionOperationSelector'
import {
  QuestionTreeSelector,
  type QuestionTreeSelectorProps,
} from '~/components/features/QuestionTreeSelector'
import { useFormField } from '~/hooks/useFormField'
import {
  type Question,
  useSurveyForSettingsQueryQuestions,
} from '~/hooks/useSurveyForSettingsQuery'
import { findByPath } from '~/utils'

import { type InputValues } from '../utils'

import styles from './ConditionInput.css'

export type ConditionInputProps = {
  conditionGroupIndex: number
  conditionIndex: number
  nineSegmentIndex: number
  onDelete: (conditionIndex: number) => void
}

export const ConditionInput = ({
  conditionGroupIndex,
  conditionIndex,
  nineSegmentIndex,
  onDelete,
}: ConditionInputProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<InputValues>()
  const namePrefix =
    `segment_definitions.${nineSegmentIndex}.condition_groups.${conditionGroupIndex}.${conditionIndex}` as const
  const questionIdField = useFormField(control, `${namePrefix}.question_id`)
  const operationField = useFormField(control, `${namePrefix}.operation`)
  const useBrandNumberAsAnswerField = useFormField(
    control,
    `${namePrefix}.use_brand_number_as_answer`,
  )
  const useBrandNumberAsQuestionNumberField = useFormField(
    control,
    `${namePrefix}.use_brand_number_as_question_number`,
  )
  const answerOptionsField = useFormField(control, `${namePrefix}.values`)
  const textAnswersField = useFormField(control, `${namePrefix}.text_values`)

  const questions = useSurveyForSettingsQueryQuestions()
  const handleQuestionChange = React.useCallback<
    EH<QuestionTreeSelectorProps<Question>, 'onChange'>
  >(
    (questionId) => {
      questionIdField.onChange(questionId)

      answerOptionsField.onChange([])
      textAnswersField.onChange([])
    },
    [answerOptionsField, questionIdField, textAnswersField],
  )

  const handleOperationChange = React.useCallback<
    EH<ConditionOperationSelectorProps, 'onChange'>
  >(
    (operation) => {
      const prevOperation = operationField.value
      const prevAnswerOptions = answerOptionsField.value

      operationField.onChange(operation)

      if (
        ['in', 'not_in'].includes(prevOperation) &&
        ['equal', 'not_equal'].includes(operation) &&
        prevAnswerOptions.length !== 1
      ) {
        const answerOptions = match(prevAnswerOptions.length)
          .with(0, () => [])
          .otherwise(() => prevAnswerOptions.slice(0, 1))
        answerOptionsField.onChange(answerOptions)
      }
    },
    [answerOptionsField, operationField],
  )

  const question = React.useMemo(
    () => findByPath(questions, 'question_id', questionIdField.value),
    [questionIdField.value, questions],
  )

  const answerType = React.useMemo(
    () =>
      match(question?.answer_type)
        .with('9segs_SAMT', '9segs_MAMT', () => 'matrix' as const)
        .with('FA', () => 'free' as const)
        .otherwise(() => 'normal' as const),
    [question?.answer_type],
  )

  const handleUseBrandNumberAsAnswerChange = React.useCallback<
    EH<CheckboxProps, 'onChange'>
  >(
    (event) => {
      useBrandNumberAsAnswerField.onChange(event)

      const checked = event.value
      if (checked) {
        answerOptionsField.onChange([])
      }
    },
    [answerOptionsField, useBrandNumberAsAnswerField],
  )

  const answerOptions = React.useMemo(
    () => question?.answer_options ?? [],
    [question?.answer_options],
  )

  const handleTextAnswerChange = React.useCallback<
    EH<StyledInputProps, 'onChange'>
  >(
    (event) => {
      textAnswersField.onChange([event.value])
    },
    [textAnswersField],
  )

  const handleDeleteButtonClick = React.useCallback<
    EH<IconButtonProps, 'onClick'>
  >(() => {
    onDelete(conditionIndex)
  }, [conditionIndex, onDelete])

  return (
    <HStack align="center" spacing={4}>
      <Box border="normal" className={styles.container} flex={1} padding={4}>
        <>
          <Typography fontSize={16} variant="normal">
            {t('questionnaire')}
          </Typography>

          <QuestionTreeSelector
            isMatrixQuestionSelectable={true}
            questions={questions}
            {...questionIdField}
            onChange={handleQuestionChange}
          />
        </>

        <>
          <Typography fontSize={16} variant="normal">
            {t('definition')}
          </Typography>

          <ConditionOperationSelector
            {...operationField}
            onChange={handleOperationChange}
          />
        </>

        <>
          <Typography fontSize={16} variant="normal">
            {t('response_value')}
          </Typography>

          {match(answerType)
            .with('normal', () => (
              <HStack align="center" justify="space-between" spacing={2}>
                <Typography fontSize={16} variant="normal">
                  {t('match_brand_number_with_response_value')}
                </Typography>

                <Checkbox
                  {...useBrandNumberAsAnswerField}
                  onChange={handleUseBrandNumberAsAnswerChange}
                />
              </HStack>
            ))
            .with('matrix', () => (
              <HStack align="center" justify="space-between" spacing={2}>
                <Typography fontSize={16} variant="normal">
                  {t('match_brand_number_with_matrix_questionnaire_number')}
                </Typography>

                <Checkbox {...useBrandNumberAsQuestionNumberField} />
              </HStack>
            ))
            .with('free', () => null)
            .exhaustive()}
        </>

        {match(answerType)
          .with('normal', 'matrix', (answerType) => (
            <AnswerOptionConditionalSelector
              answerOptions={answerOptions}
              className={styles.answerOptionsInput}
              conditionOperation={operationField.value}
              disabled={match(answerType)
                .with('normal', () => useBrandNumberAsAnswerField.value)
                .with('matrix', () => false)
                .exhaustive()}
              {...answerOptionsField}
            />
          ))
          .with('free', () => (
            <StyledInput
              placeholder={t('please_enter')}
              width="100%"
              {...textAnswersField}
              value={textAnswersField.value[0] ?? ''}
              onChange={handleTextAnswerChange}
            />
          ))
          .exhaustive()}
      </Box>

      <IconButton
        buttonStyle="light"
        fillMode="flat"
        icon="delete"
        onClick={handleDeleteButtonClick}
      />
    </HStack>
  )
}
